import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextArea, TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  ArrowDownLine,
  ArrowUpLine,
  DropboxDashLine,
  LineViewLine,
  LinkLine,
  RedoLine,
  UndoLine,
} from '@dropbox/dig-icons/assets';
import * as Sentry from '@sentry/react';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {WORKSTREAM_STATUS_CHAT_STOP} from 'analytics/keys';
import {CoachSubmissionContext, Workstream} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusChip} from 'components/DSYS/StatusChip';
import {StopSquare} from 'components/DSYS/StopSquare';
import {t} from 'i18next';
import {$getRoot} from 'lexical';
import {Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {getWorkstreamUpdateVisualStatus} from './utils';
import {convertEditorJsonToMarkdown} from './WorkstreamAICoachTab';
import {
  AssistGuardrailChatRow,
  type AssistPostGate,
  useWorkstreamAssist,
  WorkstreamAssistPinnedStrip,
} from './WorkstreamAssistPanel';
import type {ChatDraftHistoryControl} from './workstreamChatDraftHistory';
import {
  contextEngineTextHasCitationMarkers,
  stripContextEngineCitationMarkers,
  WorkstreamChatContextAttachmentsMenuChip,
  WorkstreamChatContextWorkstreamChip,
  WorkstreamMarkdownWithCitationChips,
} from './WorkstreamChatSourceChips';
import {WorkstreamClarificationMessage} from './WorkstreamClarificationMessage';
import type {
  ClarificationAnswersDisplay,
  ContextEngineProgressStep,
  ContextEngineSource,
} from './workstreamContextEngineChatApi';
import {
  buildWorkstreamContextEngineChatPayload,
  clarificationAnswersDisplayFromParsed,
  type ClarificationPayload,
  clarificationPayloadToWizardState,
  contextAttachmentsFromWorkstreamLinks,
  contextEngineChatErrorMessage,
  contextEngineStreamProgressStepsFromFrame,
  expandSingleNewlinesToParagraphBreaks,
  getAssistantTextFromResponse,
  getStructuredResponseFromChat,
  isAllowedHttpUrl,
  isContextEngineStreamAbortError,
  MAX_WORKSTREAM_CONTEXT_ATTACHMENTS,
  parseClarificationUserMessage,
  simpleDraftHash,
  streamWorkstreamContextEngineChat,
  type WorkstreamContextEngineChatResponse,
} from './workstreamContextEngineChatApi';
import styles from './WorkstreamStatusChat.module.css';
import {
  historyThinkingSteps,
  WorkstreamContextEngineThinkingPanel,
  WorkstreamContextEngineThoughtSecondsCaption,
} from './WorkstreamStatusChatThinking';

function isDisplayableStatus(status: string | undefined): boolean {
  if (!status || typeof status !== 'string') return false;
  const s = status.trim();
  return s.length > 0 && !s.startsWith('_') && !s.includes('normalize');
}

/** Within this many px of the scroll bottom counts as "near bottom" for hints / intersection (rounding / padding). */
const CHAT_THREAD_BOTTOM_PIN_PX = 32;
/** Auto-follow stream only while this close to the true bottom; hysteresis avoids fighting the user slightly off max scroll. */
const CHAT_THREAD_FOLLOW_RESUME_PX = 8;
/** Stop auto-follow once the user scrolls this far up from the bottom. */
const CHAT_THREAD_FOLLOW_RELEASE_PX = 48;

const WorkstreamAssistantMarkdown = ({
  value,
  sources,
  withLinks,
  paddingY,
}: {
  value: string;
  sources?: ContextEngineSource[];
  withLinks?: boolean;
  paddingY?: boolean;
}) => (
  <WorkstreamMarkdownWithCitationChips
    value={value}
    sources={sources}
    withLinks={withLinks}
    paddingY={paddingY}
    citationChipsInteractive={false}
    showCitationMarkers={false}
  />
);

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Assistant: raw text for history. User: full text sent to API when it differs from `text` (e.g. coach iterate context). */
  rawTextForHistory?: string;
  status?: string;
  isDraftSuggestion?: boolean;
  /** Seconds the assistant took to respond (shown as "Thought for X seconds"). */
  thoughtSeconds?: number;
  /** Sources cited by the assistant (e.g. Paper, Jira). */
  sources?: ContextEngineSource[];
  hidden?: boolean;
  /** How to render assistant turns after auto-apply / clarify flow. */
  assistantKind?: 'clarification' | 'draft_applied' | 'assistant_chat';
  /** Prior status when `draft_applied` and status changed (shown as before → after). */
  draftPreviousStatus?: string;
  /** Optional follow-up user messages returned by Context Engine. */
  suggestedQueries?: string[];
  /** After user submits structured clarification answers, hide the question UI. */
  clarificationResolved?: boolean;
  /** Structured clarification wizard (schema v1); server-normalized from assistant text. */
  clarification?: ClarificationPayload;
  /** Inline Q&A for user clarification replies (avoids showing wire `###` header in the bubble). */
  clarificationAnswersDisplay?: ClarificationAnswersDisplay;
  /** Monotonic key for UI state (e.g. thinking panel expansion). */
  threadKey?: number;
  /** Context Engine stream progress steps captured for this turn. */
  thinkingSteps?: ContextEngineProgressStep[];
}

function resolveClarificationUserDisplay(msg: ChatMessage): ClarificationAnswersDisplay | null {
  if (msg.clarificationAnswersDisplay) {
    return msg.clarificationAnswersDisplay;
  }
  const parsed = parseClarificationUserMessage(msg.text);
  if (!parsed) return null;
  return clarificationAnswersDisplayFromParsed(parsed);
}

function clarificationUserDisplayHasContent(d: ClarificationAnswersDisplay): boolean {
  return Boolean(d.intro?.trim() || d.pairs.length > 0 || d.optionalNote?.trim());
}

/** One logical user message rendered as alternating left (intro / questions) and right (answers) rows. */
function isAlternatingClarificationUserMessage(msg: ChatMessage): boolean {
  if (msg.role !== 'user') return false;
  const clar = resolveClarificationUserDisplay(msg);
  return Boolean(clar && clarificationUserDisplayHasContent(clar));
}

function findLastActiveClarification(list: ChatMessage[]): {
  messageIndex: number;
  parsed: ReturnType<typeof clarificationPayloadToWizardState>;
} | null {
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i];
    if (m.role === 'assistant' && m.assistantKind === 'clarification' && !m.clarificationResolved) {
      const payload = m.clarification;
      if (payload?.questions?.length) {
        return {messageIndex: i, parsed: clarificationPayloadToWizardState(payload)};
      }
    }
  }
  return null;
}

function isAssistantClarificationWithQuestions(m: ChatMessage): boolean {
  return (
    m.role === 'assistant' &&
    m.assistantKind === 'clarification' &&
    Boolean(m.clarification?.questions?.length)
  );
}

/** Optional “anything else?” step only on the first clarification-with-questions turn in this thread. */
function shouldIncludeClarificationOptionalStep(
  list: ChatMessage[],
  activeMessageIndex: number
): boolean {
  for (let i = 0; i < activeMessageIndex; i++) {
    if (isAssistantClarificationWithQuestions(list[i])) {
      return false;
    }
  }
  return true;
}

export const WorkstreamStatusChat = ({
  workstream,
  draftText,
  selectedStatus,
  onCoachContextChange,
  onApplySuggestionToDraft,
  onChatPersistenceFingerprintChange,
  onAgentInteractionChange,
  onHasInputChange,
  chatDraftHistoryControl,
  disableAssist = false,
  autoGenerateSignal,
  autoGeneratePrompt,
  hideContextControls = false,
  deferDraftApply = false,
  scrollHeaderContent,
}: {
  workstream: Workstream;
  draftText?: string;
  selectedStatus?: string;
  /** When true, coach/assist guardrails are disabled (autodraft mode). */
  disableAssist?: boolean;
  /**
   * Autodraft mode: hide the composer context controls (the add-doc "+" button and
   * the attached-link chips) to focus the experience on editing. Links seeded from
   * the workstream still power the draft; they're just not shown/editable here.
   */
  hideContextControls?: boolean;
  /**
   * Bumped by the parent to auto-send {@link autoGeneratePrompt} as a hidden user
   * message once (e.g. right after autodraft setup completes), so the assistant
   * streams a first draft without the user typing. Ignored while falsy.
   */
  autoGenerateSignal?: number;
  /** Hidden prompt sent when {@link autoGenerateSignal} changes to a new truthy value. */
  autoGeneratePrompt?: string;
  /**
   * Hold applying a freshly generated draft to the editor while true (e.g. the Dash
   * "ready" screen is generating in the background). The most recent draft is buffered
   * and flushed to {@link onApplySuggestionToDraft} the moment this flips back to false.
   */
  deferDraftApply?: boolean;
  /** Content rendered inside the scroll area above messages (e.g. the draft-ready empty state). */
  scrollHeaderContent?: React.ReactNode;
  onCoachContextChange?: (ctx: CoachSubmissionContext | null) => void;
  onApplySuggestionToDraft?: (
    markdown: string,
    status?: string,
    meta?: {ceSources?: ContextEngineSource[]}
  ) => void;
  /** Serialized chat state for modal unsaved-changes (messages, composer, attachments, conversation). */
  onChatPersistenceFingerprintChange?: (fingerprint: string) => void;
  /** Fired when the user actively interacts with the agent (e.g., sends a chat message). */
  onAgentInteractionChange?: (used: boolean) => void;
  /** Fired when the composer input transitions between empty and non-empty. */
  onHasInputChange?: (hasInput: boolean) => void;
  /** Undo/redo snapshots when the assistant overwrites the status draft (separate from editor ⌘Z). */
  chatDraftHistoryControl?: ChatDraftHistoryControl;
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const [conversationId, setConversationId] = useState('');
  const conversationIdRef = useRef(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  const [inputValue, setInputValue] = useState('');
  /** Bumping remounts the composer Lexical surface after cancel-stop so markdown ``value`` reloads. */
  const [composerRemountKey, setComposerRemountKey] = useState(0);
  const [hasInput, setHasInput] = useState(false);
  useEffect(() => {
    onHasInputChange?.(hasInput);
  }, [hasInput, onHasInputChange]);
  const [loading, setLoading] = useState(false);
  const [ceThinkingSteps, setCeThinkingSteps] = useState<ContextEngineProgressStep[]>([]);
  const [streamingThinkingExpanded, setStreamingThinkingExpanded] = useState(true);
  const [thinkingExpandedByThreadKey, setThinkingExpandedByThreadKey] = useState<
    Record<number, boolean>
  >({});
  const [userContextAttachments, setUserContextAttachments] = useState<
    Array<{url: string; title: string}>
  >([]);
  /** Workstream-link URLs already folded into attachments (so a links refetch never re-adds them). */
  const seededLinkUrlsRef = useRef<Set<string>>(new Set());
  /** Attachment URLs the user removed this session (so a links refetch doesn't re-add them). */
  const dismissedAttachmentUrlsRef = useRef<Set<string>>(new Set());
  const [addDocModalOpen, setAddDocModalOpen] = useState(false);
  const [addDocUrl, setAddDocUrl] = useState('');
  const [addDocError, setAddDocError] = useState('');
  const [addDocUrlInvalid, setAddDocUrlInvalid] = useState(false);
  const [customInstructionsModalOpen, setCustomInstructionsModalOpen] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [customInstructionsDraft, setCustomInstructionsDraft] = useState('');
  const assistGateRef = useRef<AssistPostGate | null>(null);
  const workstreamIdRef = useRef(workstream.id);
  useEffect(() => {
    workstreamIdRef.current = workstream.id;
  }, [workstream.id]);
  const inputRef = useRef<{
    getEditorState: () => {toJSON: () => object};
    clearEditor: (v?: string) => void;
  }>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const assistSuggestionsRef = useRef<HTMLDivElement>(null);
  const [assistPinned, setAssistPinned] = useState(false);
  const [assistPinExpanded, setAssistPinExpanded] = useState(false);
  /** Bumped only when the user submits a message so we scroll the thread without fighting user scroll during load/streaming. */
  const [scrollToBottomNonce, setScrollToBottomNonce] = useState(0);
  /** Bottom sentinel visible inside the scroll viewport (drives jump-to-latest affordance). */
  const [threadEndInView, setThreadEndInView] = useState(true);
  /**
   * While streaming: true only after new thread height (thinking steps, etc.) arrived while the user
   * was not following the bottom. Cleared when they reach the bottom again so scrolling up does not
   * re-show the jump affordance until more stream content arrives below.
   */
  const [unseenStreamTail, setUnseenStreamTail] = useState(false);
  /**
   * True while the user is within a few pixels of the bottom (UI / unseen-tail heuristics).
   */
  const pinnedToBottomRef = useRef(true);
  /**
   * When true, streaming/content growth may auto-scroll to the latest. Separate from `pinnedToBottomRef`
   * so a small offset from max scroll does not keep yanking the viewport during busy streams.
   */
  const followStreamTailRef = useRef(true);
  /** Ignore scroll sync while we adjust `scrollTop` ourselves (avoids clearing follow mid-layout). */
  const programmaticThreadScrollRef = useRef(false);
  /** Previous `scrollHeight` of the thread viewport; used to stick to bottom only when content grows while pinned. */
  const prevThreadScrollHeightRef = useRef(0);
  const lastAssistFeedbackDigestRef = useRef<string>('');
  /** Coach iterate feedback to prepend to the next outgoing chat API message (not shown in the thread). */
  const pendingAssistIterateContextRef = useRef<string>('');

  const ceStreamFrameCountRef = useRef(0);
  const ceThinkingStepsRef = useRef<ContextEngineProgressStep[]>([]);
  const threadKeySeqRef = useRef(0);
  const allocThreadKey = useCallback(() => {
    threadKeySeqRef.current += 1;
    return threadKeySeqRef.current;
  }, []);

  const cePendingRef = useRef<{
    wsAtSend: number;
    startMs: number;
    pendingCoach: boolean;
    selectedStatusAtSend: string;
  } | null>(null);
  const onApplySuggestionToDraftRef = useRef(onApplySuggestionToDraft);
  onApplySuggestionToDraftRef.current = onApplySuggestionToDraft;

  // While `deferDraftApply` is true we hold the newest generated draft here instead of
  // writing it into the editor, then flush it once the gate opens (see effect below).
  const deferDraftApplyRef = useRef(deferDraftApply);
  deferDraftApplyRef.current = deferDraftApply;
  const deferredDraftRef = useRef<{markdown: string; status?: string} | null>(null);
  useEffect(() => {
    if (deferDraftApply) return;
    const pending = deferredDraftRef.current;
    if (!pending) return;
    deferredDraftRef.current = null;
    onApplySuggestionToDraftRef.current?.(pending.markdown, pending.status);
  }, [deferDraftApply]);

  const ceStreamAbortRef = useRef<AbortController | null>(null);
  /** Display text of the in-flight user bubble; used to pop it and restore the composer on Stop. */
  const pendingCancelledDisplayTextRef = useRef<string | null>(null);

  const chatPersistenceFingerprint = useMemo(() => {
    const visible = messages.filter((m) => !m.hidden);
    const bodies = visible.map((m) => {
      const raw = (m.rawTextForHistory ?? m.text ?? '').trim();
      return `${m.role}:${simpleDraftHash(raw.slice(0, 8000))}`;
    });
    // Only what the user wrote: attachments are seeded from workstream links after mount.
    return JSON.stringify({m: bodies, composer: hasInput});
  }, [messages, hasInput]);

  const onChatPersistenceFingerprintChangeRef = useRef(onChatPersistenceFingerprintChange);
  onChatPersistenceFingerprintChangeRef.current = onChatPersistenceFingerprintChange;
  useEffect(() => {
    onChatPersistenceFingerprintChangeRef.current?.(chatPersistenceFingerprint);
  }, [chatPersistenceFingerprint]);

  const handleCeChatCompleted = useCallback(
    (response: WorkstreamContextEngineChatResponse) => {
      const thinkingSnapshot = ceThinkingStepsRef.current;
      ceThinkingStepsRef.current = [];
      setCeThinkingSteps([]);
      setStreamingThinkingExpanded(true);
      const pending = cePendingRef.current;
      cePendingRef.current = null;
      setLoading(false);
      if (!pending) {
        return;
      }
      if (pending.wsAtSend !== workstreamIdRef.current) {
        return;
      }
      if (pending.pendingCoach) {
        pendingAssistIterateContextRef.current = '';
        lastAssistFeedbackDigestRef.current = '';
      }
      const structured = getStructuredResponseFromChat(
        response as WorkstreamContextEngineChatResponse
      );
      const rawAssistantText = getAssistantTextFromResponse(
        response as WorkstreamContextEngineChatResponse
      );
      if (response?.conversation_id) {
        setConversationId(String(response.conversation_id));
      }
      const thoughtSeconds = Math.round((Date.now() - pending.startMs) / 1000);
      const clarificationPayload = structured.clarification;
      const hasClarification = Boolean(clarificationPayload?.questions?.length);
      const serverDraft =
        Boolean(structured.is_draft_suggestion) && Boolean((structured.comment ?? '').trim());
      const applyMarkdown = serverDraft ? (structured.comment ?? '').trim() : '';
      const effectiveIsDraft = Boolean(applyMarkdown) && serverDraft;
      const applyStatus = structured.status;
      const newStatusTrimmed = (applyStatus ?? '').trim();
      const previousStatusTrimmed = (pending.selectedStatusAtSend ?? '').trim();
      const draftStatusChanged =
        effectiveIsDraft && Boolean(newStatusTrimmed) && newStatusTrimmed !== previousStatusTrimmed;
      const hasAssistantBody =
        hasClarification || Boolean(structured.comment?.trim()) || Boolean(rawAssistantText.trim());

      // eslint-disable-next-line no-console
      console.log('[workstream-chat] draft response', {
        rawDraft: (response as Record<string, unknown>)?.draft,
        structured,
        rawAssistantText,
        serverDraft,
        effectiveIsDraft,
        applyStatus,
        draftStatusChanged,
        applyMarkdownPreview: applyMarkdown.slice(0, 300),
        willApplyToEditor: effectiveIsDraft && Boolean(applyMarkdown),
      });

      if (hasAssistantBody) {
        let assistantKind: ChatMessage['assistantKind'] = 'assistant_chat';
        if (effectiveIsDraft) {
          assistantKind = 'draft_applied';
        } else if (hasClarification) {
          assistantKind = 'clarification';
        }
        const displayTextRaw =
          assistantKind === 'draft_applied'
            ? rawAssistantText.trim() || t('workstream_chat_draft_updated_editor')
            : hasClarification
              ? ''
              : structured.comment.trim() ||
                rawAssistantText.trim() ||
                t('workstream_chat_empty_response');
        const displayText = expandSingleNewlinesToParagraphBreaks(displayTextRaw);
        const threadKey = allocThreadKey();
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          text: displayText,
          thoughtSeconds,
          threadKey,
          ...(thinkingSnapshot.length > 0 ? {thinkingSteps: thinkingSnapshot} : {}),
          ...(rawAssistantText && {rawTextForHistory: rawAssistantText}),
          ...(effectiveIsDraft
            ? draftStatusChanged
              ? {status: newStatusTrimmed, draftPreviousStatus: previousStatusTrimmed}
              : {}
            : applyStatus
              ? {status: applyStatus}
              : {}),
          ...(effectiveIsDraft ? {isDraftSuggestion: true} : {}),
          ...(structured.suggested_queries?.length
            ? {suggestedQueries: structured.suggested_queries}
            : {}),
          ...(structured.sources && structured.sources.length > 0 && {sources: structured.sources}),
          ...(clarificationPayload && {clarification: clarificationPayload}),
          assistantKind,
        };
        setMessages((prev) => [...prev, assistantMsg]);
        if (effectiveIsDraft && onApplySuggestionToDraftRef.current && applyMarkdown) {
          let markdownForEditor = applyMarkdown;
          if (contextEngineTextHasCitationMarkers(applyMarkdown)) {
            try {
              markdownForEditor = stripContextEngineCitationMarkers(applyMarkdown);
            } catch {
              markdownForEditor = applyMarkdown;
            }
          }
          // eslint-disable-next-line no-console
          console.log('[workstream-chat] applying draft to editor', {
            applyStatus,
            markdownForEditor,
            deferred: deferDraftApplyRef.current,
          });
          if (deferDraftApplyRef.current) {
            // Ready screen is showing: buffer the draft and let the release effect
            // flush it into the editor once the user reveals the chat.
            deferredDraftRef.current = {markdown: markdownForEditor, status: applyStatus};
          } else {
            onApplySuggestionToDraftRef.current(markdownForEditor, applyStatus);
          }
          if (disableAssist) {
            analyticsLogger().logEvent('AUTODRAFT_DRAFT_GENERATION_COMPLETE', {
              workstream_id: String(workstream.id),
              success: 'true',
              draft_length: String(markdownForEditor.length),
            });
          }
        }
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: t('workstream_chat_empty_response'),
            thoughtSeconds,
            threadKey: allocThreadKey(),
            ...(thinkingSnapshot.length > 0 ? {thinkingSteps: thinkingSnapshot} : {}),
            assistantKind: 'assistant_chat',
          },
        ]);
      }
    },
    [allocThreadKey, disableAssist, workstream.id]
  );

  const chatBusy = loading;

  useEffect(() => {
    ceStreamAbortRef.current?.abort();
    ceStreamAbortRef.current = null;
    pendingCancelledDisplayTextRef.current = null;
    setLoading(false);
    setComposerRemountKey(0);
    lastAssistFeedbackDigestRef.current = '';
    pendingAssistIterateContextRef.current = '';
    setMessages([]);
    setConversationId('');
    setAssistPinned(false);
    setAssistPinExpanded(false);
    ceThinkingStepsRef.current = [];
    setCeThinkingSteps([]);
    setStreamingThinkingExpanded(true);
    setThinkingExpandedByThreadKey({});
    threadKeySeqRef.current = 0;
    setScrollToBottomNonce(0);
    setThreadEndInView(true);
    setUnseenStreamTail(false);
    pinnedToBottomRef.current = true;
    followStreamTailRef.current = true;
    prevThreadScrollHeightRef.current = 0;
    const seededAttachments = contextAttachmentsFromWorkstreamLinks(
      workstream.links,
      MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
    );
    seededLinkUrlsRef.current = new Set(seededAttachments.map((a) => a.url));
    dismissedAttachmentUrlsRef.current = new Set();
    setUserContextAttachments(seededAttachments);
    // Links are read when workstream id changes only (avoid resetting chat when links refetch in place).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstream.id]);

  // Fold in workstream links that arrive after mount (e.g. links added in autodraft setup are
  // saved via a workstream edit that refetches in place, without changing the workstream id) so
  // they still surface as chat resources and get sent to the agent — without clobbering the
  // user's manual adds/removes in this session.
  useEffect(() => {
    const links = contextAttachmentsFromWorkstreamLinks(
      workstream.links,
      MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
    );
    setUserContextAttachments((prev) => {
      const additions: Array<{url: string; title: string}> = [];
      for (const link of links) {
        if (seededLinkUrlsRef.current.has(link.url)) continue;
        if (dismissedAttachmentUrlsRef.current.has(link.url)) continue;
        seededLinkUrlsRef.current.add(link.url);
        if (prev.some((a) => a.url === link.url)) continue;
        if (prev.length + additions.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS) break;
        additions.push(link);
      }
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [workstream.links]);

  const openAddDocModal = useCallback(() => {
    setAddDocError('');
    setAddDocUrlInvalid(false);
    setAddDocUrl('');
    setAddDocModalOpen(true);
  }, []);

  const confirmAddDoc = useCallback(() => {
    const rawUrl = addDocUrl.trim();
    if (!isAllowedHttpUrl(rawUrl)) {
      setAddDocUrlInvalid(true);
      setAddDocError('');
      return;
    }
    setAddDocUrlInvalid(false);
    if (userContextAttachments.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS) {
      setAddDocError(
        t('workstream_chat_add_context_doc_limit', {max: MAX_WORKSTREAM_CONTEXT_ATTACHMENTS})
      );
      return;
    }
    if (userContextAttachments.some((a) => a.url === rawUrl)) {
      setAddDocError(t('workstream_chat_add_context_doc_duplicate'));
      return;
    }
    setUserContextAttachments((prev) => [...prev, {url: rawUrl, title: rawUrl}]);
    setAddDocModalOpen(false);
    setAddDocUrl('');
    setAddDocError('');
    setAddDocUrlInvalid(false);
  }, [addDocUrl, userContextAttachments]);

  const handleAssistIterateFeedback = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed === lastAssistFeedbackDigestRef.current) return;
    lastAssistFeedbackDigestRef.current = trimmed;
    pendingAssistIterateContextRef.current = trimmed;
  }, []);

  const handleAssistPostGateChange = useCallback((gate: AssistPostGate | null) => {
    assistGateRef.current = gate;
  }, []);

  const {
    guardrails,
    jiraObjects,
    isInitialPolling: assistInitialPolling,
    satisfiedCount,
    total: assistTotal,
    showAssistThread,
  } = useWorkstreamAssist({
    workstream,
    draftText,
    selectedStatus,
    onAssistIterateFeedback: handleAssistIterateFeedback,
    onAssistPostGateChange: handleAssistPostGateChange,
    onCoachContextChange,
    enabled: !disableAssist,
  });

  const scrollThreadToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    programmaticThreadScrollRef.current = true;
    // Only move the thread viewport. Avoid scrollIntoView — it walks all scrollable ancestors and
    // fights the user when a nested overflow container exists (or after they scrolled inside one).
    el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    pinnedToBottomRef.current = true;
    followStreamTailRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        programmaticThreadScrollRef.current = false;
      });
    });
  }, []);

  const syncPinnedToBottomFromScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottomRef.current = distance <= CHAT_THREAD_BOTTOM_PIN_PX;
    if (programmaticThreadScrollRef.current) return;
    if (distance > CHAT_THREAD_FOLLOW_RELEASE_PX) {
      followStreamTailRef.current = false;
    } else if (distance <= CHAT_THREAD_FOLLOW_RESUME_PX) {
      followStreamTailRef.current = true;
    }
  }, []);

  useLayoutEffect(() => {
    if (scrollToBottomNonce === 0) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      scrollThreadToBottom();
    };
    run();
    const raf = requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    const scrollRetryTimeoutId = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(scrollRetryTimeoutId);
    };
  }, [scrollToBottomNonce, scrollThreadToBottom]);

  useLayoutEffect(() => {
    if (!scrollHeaderContent) return;
    scrollThreadToBottom();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => syncPinnedToBottomFromScroll();
    el.addEventListener('scroll', onScroll, {passive: true});
    syncPinnedToBottomFromScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [syncPinnedToBottomFromScroll, workstream.id]);

  /**
   * Keep the viewport pinned only when the user is already at the bottom (or was pinned before this
   * layout pass grew `scrollHeight`). Do not use `followStreamTailRef` alone here — it updates on
   * scroll events and can stay true for a frame after the user scrolls up, which fights the user
   * while thinking steps stream in.
   */
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const prevH = prevThreadScrollHeightRef.current;
    const h = el.scrollHeight;
    const bottomEdge = el.scrollTop + el.clientHeight;
    const wasPinnedToContentBottom =
      prevH > 0 && bottomEdge >= prevH - CHAT_THREAD_FOLLOW_RELEASE_PX;
    const distanceNow = h - el.scrollTop - el.clientHeight;
    const atBottomNow = distanceNow <= CHAT_THREAD_FOLLOW_RELEASE_PX;
    const shouldStickToTail = atBottomNow || wasPinnedToContentBottom;

    if (shouldStickToTail) {
      scrollThreadToBottom();
    } else {
      followStreamTailRef.current = false;
    }

    const elAfter = scrollContainerRef.current;
    if (elAfter) {
      prevThreadScrollHeightRef.current = elAfter.scrollHeight;
    }
  }, [
    ceThinkingSteps,
    messages.length,
    chatBusy,
    streamingThinkingExpanded,
    showAssistThread,
    assistTotal,
    assistInitialPolling,
    scrollThreadToBottom,
  ]);

  /** Arm jump-to-latest during streaming only when content grows while the user is not following the tail. */
  useLayoutEffect(() => {
    if (!chatBusy) return;
    if (!followStreamTailRef.current) {
      setUnseenStreamTail(true);
    }
  }, [
    chatBusy,
    ceThinkingSteps,
    messages.length,
    streamingThinkingExpanded,
    showAssistThread,
    assistTotal,
    assistInitialPolling,
  ]);

  useEffect(() => {
    if (!chatBusy) {
      setUnseenStreamTail(false);
      return;
    }
    if (threadEndInView) {
      setUnseenStreamTail(false);
    }
  }, [chatBusy, threadEndInView]);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const target = messagesEndRef.current;
    if (!root || !target) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setThreadEndInView(entry.isIntersecting);
      },
      {root, rootMargin: '0px 0px 12px 0px', threshold: 0}
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    messages.length,
    chatBusy,
    showAssistThread,
    assistTotal,
    assistInitialPolling,
    ceThinkingSteps.length,
    streamingThinkingExpanded,
  ]);

  useEffect(() => {
    if (!assistPinned) {
      setAssistPinExpanded(false);
    }
  }, [assistPinned]);

  useEffect(() => {
    if (!showAssistThread) {
      setAssistPinned(false);
      return;
    }
    const root = scrollContainerRef.current;
    const target = assistSuggestionsRef.current;
    if (!root || !target) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const rootEl = scrollContainerRef.current;
        if (!rootEl) return;
        const rootRect = rootEl.getBoundingClientRect();
        const offAbove = entry.boundingClientRect.bottom < rootRect.top;
        const hasThreadBelow = messagesRef.current.length > 0 || chatBusy || loading;
        setAssistPinned(offAbove && hasThreadBelow);
      },
      {root, threshold: 0}
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [showAssistThread, assistTotal, messages.length, chatBusy, loading]);

  const getInputMarkdown = useCallback((): string => {
    const state = inputRef.current?.getEditorState?.() as {toJSON: () => object} | undefined;
    if (!state) return '';
    try {
      return convertEditorJsonToMarkdown(JSON.stringify(state.toJSON())) ?? '';
    } catch {
      return '';
    }
  }, []);

  const handleStopStreaming = useCallback(() => {
    analyticsLogger().logEvent(WORKSTREAM_STATUS_CHAT_STOP, {
      workstream_id: workstream.id,
    });
    ceStreamAbortRef.current?.abort();
  }, [workstream.id]);

  const draftMarkdown = draftText ? convertEditorJsonToMarkdown(draftText) : '';

  const previousPublishedUpdateMarkdown = useMemo(() => {
    const prior = workstream.updates?.[1];
    if (!prior) {
      return '';
    }
    const raw = (prior.comment ?? '').trim();
    if (!raw.length) {
      return '';
    }
    return (convertEditorJsonToMarkdown(raw) ?? '').trim();
  }, [workstream.updates]);

  const sendUserMessage = useCallback(
    async (
      userText: string,
      options?: {
        hiddenUser?: boolean;
        fromClarification?: boolean;
        clarificationMessageIndex?: number;
        clarificationDisplay?: ClarificationAnswersDisplay;
      }
    ) => {
      if (chatBusy) return;
      pinnedToBottomRef.current = true;
      followStreamTailRef.current = true;
      setScrollToBottomNonce((n) => n + 1);
      const wsAtSend = workstream.id;
      const pendingCoach = pendingAssistIterateContextRef.current.trim();
      const apiMessage =
        pendingCoach.length > 0
          ? `${t('workstream_assist_iterate_feedback_prefix')}\n\n${pendingCoach}\n\n${userText}`
          : userText;
      const history = messagesRef.current.map((m) =>
        m.role === 'user'
          ? {
              message_type: {
                user_message: {text: m.rawTextForHistory ?? m.text},
              },
            }
          : {
              message_type: {
                assistant_message: {
                  text: m.rawTextForHistory ?? m.text,
                },
              },
            }
      );
      setMessages((prev) => {
        let next = prev;
        if (options?.clarificationMessageIndex != null) {
          next = prev.map((m, i) =>
            i === options.clarificationMessageIndex ? {...m, clarificationResolved: true} : m
          );
        }
        return [
          ...next,
          {
            role: 'user',
            text: userText,
            ...(apiMessage !== userText ? {rawTextForHistory: apiMessage} : {}),
            ...(options?.hiddenUser ? {hidden: true} : {}),
            ...(options?.clarificationDisplay
              ? {clarificationAnswersDisplay: options.clarificationDisplay}
              : {}),
          },
        ];
      });
      const requestStartMs = Date.now();
      cePendingRef.current = {
        wsAtSend,
        startMs: requestStartMs,
        pendingCoach: pendingCoach.length > 0,
        selectedStatusAtSend: selectedStatus ?? '',
      };
      setLoading(true);
      ceThinkingStepsRef.current = [];
      setCeThinkingSteps([]);
      setStreamingThinkingExpanded(true);
      ceStreamFrameCountRef.current = 0;
      const abortController = new AbortController();
      ceStreamAbortRef.current = abortController;
      pendingCancelledDisplayTextRef.current = userText;
      const payload = buildWorkstreamContextEngineChatPayload({
        message: apiMessage,
        conversation_id: conversationIdRef.current,
        history: history.length > 0 ? history : undefined,
        draft_markdown: draftMarkdown,
        selected_status: selectedStatus ?? '',
        draft_content_hash: simpleDraftHash(draftMarkdown),
        assist_guardrails: assistGateRef.current?.snapshotForChat ?? null,
        user_attachments: userContextAttachments.map((a) => ({
          url: a.url,
          title: (a.title || a.url).trim() || a.url,
        })),
        previous_update_markdown: previousPublishedUpdateMarkdown,
      });
      try {
        const result = await streamWorkstreamContextEngineChat(wsAtSend, payload, {
          signal: abortController.signal,
          onFrame: (p) => {
            ceStreamFrameCountRef.current += 1;
            const steps = contextEngineStreamProgressStepsFromFrame(p);
            ceThinkingStepsRef.current = steps;
            setCeThinkingSteps(steps);
          },
        });
        if (ceStreamFrameCountRef.current > 0) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, 0);
          });
        }
        pendingCancelledDisplayTextRef.current = null;
        ceStreamAbortRef.current = null;
        handleCeChatCompleted(result);
      } catch (err: unknown) {
        ceStreamAbortRef.current = null;
        if (isContextEngineStreamAbortError(err)) {
          const cancelledDisplayText = pendingCancelledDisplayTextRef.current;
          pendingCancelledDisplayTextRef.current = null;
          setLoading(false);
          cePendingRef.current = null;
          ceThinkingStepsRef.current = [];
          setCeThinkingSteps([]);
          setStreamingThinkingExpanded(true);
          setMessages((prev) => {
            if (!cancelledDisplayText || prev.length === 0) {
              return prev;
            }
            const last = prev[prev.length - 1];
            if (last.role !== 'user' || last.text !== cancelledDisplayText) {
              return prev;
            }
            return prev.slice(0, -1);
          });
          if (cancelledDisplayText != null && cancelledDisplayText.length > 0) {
            setInputValue(cancelledDisplayText);
            setHasInput(true);
            setComposerRemountKey((k) => k + 1);
          }
          return;
        }
        pendingCancelledDisplayTextRef.current = null;
        cePendingRef.current = null;
        setLoading(false);
        ceThinkingStepsRef.current = [];
        setCeThinkingSteps([]);
        setStreamingThinkingExpanded(true);
        const thoughtSeconds = Math.round((Date.now() - requestStartMs) / 1000);
        const message = contextEngineChatErrorMessage(err) || t('workstream_chat_error');
        Sentry.captureException(err instanceof Error ? err : new Error(message), {
          tags: {feature: 'workstream_context_engine_chat'},
          extra: {workstream_id: workstream.id, thought_seconds: thoughtSeconds},
        });
        setMessages((prev) => [
          ...prev,
          {role: 'assistant', text: message, thoughtSeconds, threadKey: allocThreadKey()},
        ]);
        if (disableAssist) {
          analyticsLogger().logEvent('AUTODRAFT_DRAFT_GENERATION_COMPLETE', {
            workstream_id: String(workstream.id),
            success: 'false',
          });
        }
      }
    },
    [
      chatBusy,
      disableAssist,
      handleCeChatCompleted,
      allocThreadKey,
      workstream.id,
      draftMarkdown,
      selectedStatus,
      userContextAttachments,
      previousPublishedUpdateMarkdown,
    ]
  );

  const handleSend = useCallback(
    async (options?: {fromEnter?: boolean}) => {
      if (chatBusy) return;
      const markdown = getInputMarkdown().trim();
      if (!markdown) return;
      onAgentInteractionChange?.(true);
      if (options?.fromEnter) {
        analyticsLogger().logEvent('WORKSTREAM_STATUS_CHAT_ENTER_SEND', {
          workstream_id: workstream.id,
        });
      }
      inputRef.current?.clearEditor?.('');
      setInputValue('');
      setHasInput(false);
      await sendUserMessage(markdown);
    },
    [chatBusy, getInputMarkdown, onAgentInteractionChange, sendUserMessage, workstream.id]
  );

  const autoGenHandledSignalRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!autoGenerateSignal || !autoGeneratePrompt) {
      return;
    }
    if (autoGenHandledSignalRef.current === autoGenerateSignal) {
      return;
    }
    // Defer to a macrotask so the send happens AFTER the chat-reset effect
    // (which aborts any in-flight stream on mount, and double-fires under
    // StrictMode). Sending synchronously here races that reset and gets the
    // request cancelled. The cleanup cancels the pending send if we unmount or
    // the signal changes first, so exactly one draft is kicked off.
    const timeoutId = window.setTimeout(() => {
      if (autoGenHandledSignalRef.current === autoGenerateSignal) {
        return;
      }
      if (messagesRef.current.length > 0) {
        // A conversation already exists (e.g. restored) — don't inject a draft prompt over it.
        autoGenHandledSignalRef.current = autoGenerateSignal;
        return;
      }
      autoGenHandledSignalRef.current = autoGenerateSignal;
      void sendUserMessage(autoGeneratePrompt, {hiddenUser: true});
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [autoGenerateSignal, autoGeneratePrompt, sendUserMessage]);

  const showStatus = (status: string | undefined) => isDisplayableStatus(status);
  const isUser = (msg: ChatMessage) => msg.role === 'user';

  const activeClarification = useMemo(() => findLastActiveClarification(messages), [messages]);

  const showBelowFoldHint = useMemo(() => {
    if (!threadEndInView) {
      if (chatBusy) {
        return unseenStreamTail;
      }
      return messages.length > 0 || showAssistThread;
    }
    return false;
  }, [threadEndInView, chatBusy, unseenStreamTail, messages.length, showAssistThread]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      backgroundColor="Background Subtle"
      paddingX="24"
      paddingTop="8"
      paddingBottom="24"
      style={{
        flex: '1 1 0%',
        minHeight: 0,
        height: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {showAssistThread && assistPinned && assistTotal > 0 ? (
        <Box width="100%" style={{flexShrink: 0, zIndex: 2}} backgroundColor="Background Subtle">
          <WorkstreamAssistPinnedStrip
            guardrails={guardrails}
            satisfiedCount={satisfiedCount}
            total={assistTotal}
            expanded={assistPinExpanded}
            onToggleExpanded={() => setAssistPinExpanded((e) => !e)}
          />
          {assistPinExpanded ? (
            <Box
              backgroundColor="Background Subtle"
              paddingX="12"
              paddingBottom="12"
              borderBottom="Solid"
              borderColor="Border Subtle"
              borderWidth="1"
              style={{maxHeight: 'min(40vh, 320px)', overflowY: 'auto'}}
            >
              <Stack gap="0">
                {guardrails.map((g, i) => (
                  <AssistGuardrailChatRow
                    key={i}
                    item={g}
                    workstream={workstream}
                    jiraObjects={jiraObjects}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Box>
      ) : null}
      <div className={styles.chatScrollArea}>
        <Box
          ref={scrollContainerRef}
          className={styles.scrollViewport}
          overflowY="auto"
          display="flex"
          flexDirection="column"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            height: 0,
            minWidth: 0,
            overscrollBehavior: 'contain',
          }}
        >
          <Box display="flex" flexDirection="column" width="100%" minHeight="100%" flexShrink={0}>
            {scrollHeaderContent ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexGrow={1}
                flexShrink={1}
                minHeight="0"
              >
                {scrollHeaderContent}
              </Box>
            ) : (
              <Box aria-hidden flexGrow={1} flexShrink={1} minHeight="0" />
            )}
            <Box
              display="flex"
              flexDirection="column"
              flexShrink={0}
              style={{width: '100%', gap: 8}}
            >
              {showAssistThread && (
                <Box
                  ref={assistSuggestionsRef}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  width="100%"
                >
                  <Box style={{minWidth: 0}}>
                    {assistInitialPolling && assistTotal === 0 ? (
                      <Box display="flex" alignItems="center" style={{gap: 8}} paddingY="4">
                        <Spinner size="xsmall" />
                        <Text color="subtle" size="small">
                          {t('workstream_assist_strip_empty')}
                        </Text>
                      </Box>
                    ) : (
                      <>
                        <Box style={{lineHeight: 1.4, marginBottom: 8}}>
                          <Text size="small">{t('workstream_assist_suggestions_intro')}</Text>
                        </Box>
                        <Stack gap="0">
                          {guardrails.map((g, i) => (
                            <AssistGuardrailChatRow
                              key={i}
                              item={g}
                              workstream={workstream}
                              jiraObjects={jiraObjects}
                            />
                          ))}
                        </Stack>
                        <Box style={{lineHeight: 1.4, marginTop: 8}}>
                          <Text size="small">{t('workstream_assist_suggestions_intro_end')}</Text>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              )}
              {messages.map((msg, i) => {
                if (msg.role === 'user' && msg.hidden) {
                  return null;
                }
                if (
                  msg.role === 'assistant' &&
                  msg.assistantKind === 'clarification' &&
                  msg.clarificationResolved
                ) {
                  return null;
                }
                return (
                  <Box
                    key={i}
                    display="flex"
                    flexDirection="column"
                    alignItems={
                      isAlternatingClarificationUserMessage(msg)
                        ? 'stretch'
                        : isUser(msg)
                          ? 'flex-end'
                          : 'flex-start'
                    }
                    width="100%"
                  >
                    {msg.role === 'assistant' &&
                    msg.thoughtSeconds != null &&
                    (!msg.thinkingSteps || msg.thinkingSteps.length === 0) ? (
                      <WorkstreamContextEngineThoughtSecondsCaption
                        thoughtSeconds={msg.thoughtSeconds}
                        withBelowSpacing
                      />
                    ) : null}
                    {msg.role === 'assistant' &&
                    msg.thinkingSteps &&
                    msg.thinkingSteps.length > 0 &&
                    msg.threadKey != null &&
                    (historyThinkingSteps(msg.thinkingSteps).length > 0 ||
                      msg.thoughtSeconds != null) ? (
                      <WorkstreamContextEngineThinkingPanel
                        steps={msg.thinkingSteps}
                        streaming={false}
                        thoughtSeconds={msg.thoughtSeconds}
                        withBelowSpacing
                        expanded={Boolean(thinkingExpandedByThreadKey[msg.threadKey])}
                        onToggleExpanded={() => {
                          const k = msg.threadKey;
                          if (k == null) return;
                          setThinkingExpandedByThreadKey((prev) => ({
                            ...prev,
                            [k]: !prev[k],
                          }));
                        }}
                      />
                    ) : null}
                    {msg.role === 'user' ? (
                      (() => {
                        const clar = resolveClarificationUserDisplay(msg);
                        if (clar && clarificationUserDisplayHasContent(clar)) {
                          return (
                            <Stack gap="8" style={{width: '100%'}}>
                              {clar.intro?.trim() ? (
                                <Box
                                  width="100%"
                                  display="flex"
                                  justifyContent="flex-start"
                                  style={{minWidth: 0}}
                                >
                                  <Box style={{maxWidth: '85%', minWidth: 0, lineHeight: 1.4}}>
                                    <RichTextArea
                                      disableBorder
                                      value={clar.intro.trim()}
                                      editable={false}
                                      textColor="inherit"
                                      textSize="small"
                                      disableTables
                                      links
                                      clickLinks
                                    />
                                  </Box>
                                </Box>
                              ) : null}
                              {clar.pairs.map((p) => (
                                <Fragment key={p.questionIndex}>
                                  <Box
                                    width="100%"
                                    display="flex"
                                    justifyContent="flex-start"
                                    style={{minWidth: 0}}
                                  >
                                    <Box style={{maxWidth: '85%', minWidth: 0, lineHeight: 1.4}}>
                                      {p.prompt.trim() ? (
                                        <RichTextArea
                                          disableBorder
                                          value={p.prompt}
                                          editable={false}
                                          textColor="inherit"
                                          textSize="small"
                                          disableTables
                                          links
                                          clickLinks
                                        />
                                      ) : (
                                        <Text
                                          size="xsmall"
                                          color="subtle"
                                          style={{display: 'block'}}
                                        >
                                          {t('workstream_chat_clarification_user_question_label', {
                                            n: p.questionIndex,
                                          })}
                                        </Text>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box
                                    width="100%"
                                    display="flex"
                                    justifyContent="flex-end"
                                    style={{minWidth: 0}}
                                  >
                                    <Box
                                      paddingX="16"
                                      paddingY="6"
                                      borderRadius="XLarge"
                                      backgroundColor="Secondary Surface"
                                      style={{maxWidth: '85%', minWidth: 0}}
                                    >
                                      <Box style={{lineHeight: 1.4}}>
                                        <RichTextArea
                                          disableBorder
                                          value={p.answer}
                                          editable={false}
                                          textColor="inherit"
                                          textSize="small"
                                          disableTables
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </Fragment>
                              ))}
                              {clar.optionalNote?.trim() ? (
                                <Box
                                  width="100%"
                                  display="flex"
                                  justifyContent="flex-end"
                                  style={{minWidth: 0}}
                                >
                                  <Box
                                    paddingX="16"
                                    paddingY="6"
                                    borderRadius="XLarge"
                                    backgroundColor="Secondary Surface"
                                    style={{maxWidth: '85%', minWidth: 0}}
                                  >
                                    <Stack gap="4">
                                      <Text size="xsmall" color="subtle">
                                        {t('workstream_chat_optional_details_label')}
                                      </Text>
                                      <Box style={{lineHeight: 1.4}}>
                                        <RichTextArea
                                          disableBorder
                                          value={clar.optionalNote.trim()}
                                          editable={false}
                                          textColor="inherit"
                                          textSize="small"
                                          disableTables
                                        />
                                      </Box>
                                    </Stack>
                                  </Box>
                                </Box>
                              ) : null}
                            </Stack>
                          );
                        }
                        return (
                          <Box
                            paddingX="16"
                            paddingY="6"
                            borderRadius="XLarge"
                            backgroundColor="Secondary Surface"
                            style={{maxWidth: '85%', minWidth: 0}}
                          >
                            <Box style={{lineHeight: 1.4}}>
                              <RichTextArea
                                disableBorder
                                value={msg.text}
                                editable={false}
                                textColor="inherit"
                                textSize="small"
                                disableTables
                              />
                            </Box>
                          </Box>
                        );
                      })()
                    ) : (
                      <Box style={{maxWidth: '85%', minWidth: 0}}>
                        {msg.assistantKind === 'draft_applied' ? (
                          <Box>
                            {msg.status &&
                              msg.draftPreviousStatus !== undefined &&
                              showStatus(msg.status) && (
                                <Box
                                  marginBottom="8"
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{gap: 6}}
                                >
                                  {msg.draftPreviousStatus &&
                                  showStatus(msg.draftPreviousStatus) ? (
                                    <>
                                      <StatusChip
                                        status={msg.draftPreviousStatus}
                                        asStatus={getWorkstreamUpdateVisualStatus(
                                          msg.draftPreviousStatus
                                        )}
                                        size="xsmall"
                                      />
                                      <Text size="xsmall" color="subtle" aria-hidden>
                                        →
                                      </Text>
                                    </>
                                  ) : null}
                                  <StatusChip
                                    status={msg.status}
                                    asStatus={getWorkstreamUpdateVisualStatus(msg.status)}
                                    size="xsmall"
                                  />
                                </Box>
                              )}
                            {msg.text.trim() ? (
                              <WorkstreamAssistantMarkdown value={msg.text} sources={msg.sources} />
                            ) : (
                              <Text size="small" color="subtle">
                                {t('workstream_chat_draft_updated_editor')}
                              </Text>
                            )}
                          </Box>
                        ) : msg.assistantKind === 'clarification' ? (
                          msg.clarification?.questions?.length ? (
                            (() => {
                              const intro = clarificationPayloadToWizardState(
                                msg.clarification!
                              ).intro.trim();
                              return intro ? (
                                <WorkstreamAssistantMarkdown
                                  value={intro}
                                  sources={msg.sources}
                                  withLinks
                                  paddingY
                                />
                              ) : null;
                            })()
                          ) : (
                            <WorkstreamAssistantMarkdown value={msg.text} sources={msg.sources} />
                          )
                        ) : (
                          <WorkstreamAssistantMarkdown value={msg.text} sources={msg.sources} />
                        )}
                        {msg.suggestedQueries?.length ? (
                          <Box display="flex" marginTop="8" style={{gap: 8, flexWrap: 'wrap'}}>
                            {msg.suggestedQueries.map((query) => (
                              <Button
                                key={query}
                                variant="transparent"
                                size="small"
                                type="button"
                                disabled={chatBusy}
                                onClick={() => {
                                  void sendUserMessage(query);
                                }}
                              >
                                {query}
                              </Button>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                );
              })}

              {chatBusy ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  width="100%"
                  style={{minWidth: 0}}
                >
                  <WorkstreamContextEngineThinkingPanel
                    steps={ceThinkingSteps}
                    expanded={streamingThinkingExpanded}
                    onToggleExpanded={() => setStreamingThinkingExpanded((e) => !e)}
                    streaming
                  />
                </Box>
              ) : null}
              <div
                ref={messagesEndRef}
                style={{height: 1, width: '100%', flexShrink: 0}}
                aria-hidden
              />
            </Box>
          </Box>
        </Box>
        <div
          className={`${styles.bottomScrollShadow}${
            showBelowFoldHint ? ` ${styles.bottomScrollShadowVisible}` : ''
          }`}
          aria-hidden
        />
        {showBelowFoldHint ? (
          <div className={styles.jumpToLatest}>
            <Tooltip placement="top" title={t('workstream_chat_scroll_to_latest')}>
              <IconButton
                size="small"
                variant="primary"
                shape="circular"
                onClick={() => scrollThreadToBottom()}
                aria-label={t('workstream_chat_scroll_to_latest')}
              >
                <UIIcon
                  src={ArrowDownLine}
                  size="small"
                  className={atoms({color: 'Background Base'})}
                />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
      </div>

      <Box
        backgroundColor="Background Base"
        borderStyle="Solid"
        borderColor="Border Subtle"
        borderWidth="1"
        borderRadius="XLarge"
        boxShadow="Floating"
        style={{flexShrink: 0, overflow: 'hidden'}}
      >
        <Box flexGrow={1}>
          {activeClarification && chatBusy ? (
            <Box display="flex" justifyContent="flex-end" paddingX="12" paddingTop="12">
              <Button variant="outline" size="small" type="button" onClick={handleStopStreaming}>
                {t('workstream_chat_stop')}
              </Button>
            </Box>
          ) : null}
          {activeClarification ? (
            <WorkstreamClarificationMessage
              embeddedInComposer
              intro={activeClarification.parsed.intro}
              questions={activeClarification.parsed.questions}
              includeOptionalStep={shouldIncludeClarificationOptionalStep(
                messages,
                activeClarification.messageIndex
              )}
              disabled={chatBusy}
              onCancel={() => {
                if (chatBusy) return;
                const idx = activeClarification.messageIndex;
                setMessages((prev) =>
                  prev.map((m, i) => (i === idx ? {...m, clarificationResolved: true} : m))
                );
              }}
              onSubmit={(composed, display) => {
                void sendUserMessage(composed, {
                  fromClarification: true,
                  clarificationMessageIndex: activeClarification.messageIndex,
                  clarificationDisplay: display,
                });
              }}
            />
          ) : null}
          <Box
            display={activeClarification ? 'none' : 'flex'}
            alignItems="flex-start"
            paddingX="12"
            paddingTop="12"
            paddingBottom="4"
            style={{gap: 12}}
          >
            <Box style={{flex: 1, minWidth: 0}}>
              <RichTextArea
                key={composerRemountKey}
                disableBorder
                ref={inputRef}
                value={inputValue}
                editable
                paddingX="0"
                textColor="inherit"
                textSize="small"
                disableTables
                plainText
                hideToolbar
                not100
                minHeight={40}
                maxHeight="min(30vh, 420px)"
                focusNextOnTabAtBlockEnd
                onEnterSubmit={() => {
                  void handleSend({fromEnter: true});
                }}
                placeholder={t('workstream_chat_ask_anything')}
                onChange={(state) => {
                  let text = '';
                  try {
                    state.read(() => {
                      text = $getRoot().getTextContent();
                    });
                  } catch {
                    // ignore
                  }
                  setHasInput(text.trim().length > 0);
                }}
              />
            </Box>
            <Box flexShrink={0}>
              {chatBusy ? (
                <Tooltip placement="top" title={t('workstream_chat_stop')}>
                  <IconButton
                    variant="borderless"
                    size="small"
                    onClick={handleStopStreaming}
                    aria-label={t('workstream_chat_stop')}
                    className={atoms({backgroundColor: 'Opacity Surface'})}
                  >
                    <StopSquare className={atoms({color: 'Text Base'})} />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton
                  variant={!hasInput ? 'opacity' : 'primary'}
                  size="small"
                  onClick={() => {
                    void handleSend();
                  }}
                  disabled={!hasInput}
                  aria-label="icon button"
                >
                  <UIIcon
                    src={ArrowUpLine}
                    className={atoms({
                      color: !hasInput ? 'Border Base' : 'Background Base',
                    })}
                  />
                </IconButton>
              )}
            </Box>
          </Box>

          {!activeClarification && (
            <Box
              display="flex"
              paddingX="12"
              paddingTop="4"
              paddingBottom="12"
              justifyContent="space-between"
            >
              <Box display="flex" flexWrap="wrap" alignItems="center" style={{gap: 8, rowGap: 8}}>
                {!hideContextControls && (
                  <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
                    {({getContentProps, getTriggerProps}) => (
                      <>
                        <IconButton
                          {...getTriggerProps()}
                          size="small"
                          variant="borderless"
                          shape="circular"
                          disabled={chatBusy}
                          aria-label={t('workstream_chat_add_context_doc_tooltip')}
                        >
                          <UIIcon
                            src={AddLine}
                            size="small"
                            className={atoms({color: 'Text Subtle'})}
                          />
                        </IconButton>
                        <Menu.Content {...getContentProps()} minWidth="200px" placement="top-start">
                          <Menu.Segment>
                            <Menu.ActionItem
                              withLeftAccessory={<UIIcon src={LinkLine} />}
                              onClick={openAddDocModal}
                              disabled={
                                userContextAttachments.length >= MAX_WORKSTREAM_CONTEXT_ATTACHMENTS
                              }
                            >
                              {t('workstream_chat_add_resource')}
                            </Menu.ActionItem>
                            <Menu.ActionItem
                              withLeftAccessory={<UIIcon src={LineViewLine} />}
                              onClick={() => {
                                setCustomInstructionsDraft(customInstructions);
                                setCustomInstructionsModalOpen(true);
                              }}
                            >
                              {t('workstream_chat_add_custom_instructions')}
                            </Menu.ActionItem>
                          </Menu.Segment>
                        </Menu.Content>
                      </>
                    )}
                  </Menu.Wrapper>
                )}
                {!hideContextControls && (
                  <WorkstreamChatContextWorkstreamChip
                    name={workstream.name}
                    tooltipTitle={`${workstream.name}\n\nThe agent has information about your workstream including metrics, recent updates, and more. The agent uses Dash to search for relevant documents and information.`}
                  />
                )}
                {!hideContextControls && (
                  <WorkstreamChatContextAttachmentsMenuChip
                    attachments={userContextAttachments}
                    disabled={chatBusy}
                    onDismiss={(url) => {
                      dismissedAttachmentUrlsRef.current.add(url);
                      setUserContextAttachments((prev) => prev.filter((x) => x.url !== url));
                    }}
                  />
                )}
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                flexShrink={0}
                style={{gap: 10}}
              >
                {chatDraftHistoryControl?.canRedo || chatDraftHistoryControl?.canUndo ? (
                  <Box display="flex" alignItems="center" style={{gap: 2}}>
                    <Tooltip placement="top" title={t('workstream_chat_draft_undo_tooltip')}>
                      <IconButton
                        variant="borderless"
                        size="small"
                        disabled={!chatDraftHistoryControl.canUndo}
                        onClick={chatDraftHistoryControl.onUndo}
                        aria-label={t('workstream_chat_draft_undo')}
                      >
                        <UIIcon
                          src={UndoLine}
                          size="small"
                          className={atoms({
                            color: !chatDraftHistoryControl.canUndo
                              ? 'Opacity Surface'
                              : 'Text Subtle',
                          })}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip placement="top" title={t('workstream_chat_draft_redo_tooltip')}>
                      <IconButton
                        variant="borderless"
                        size="small"
                        disabled={!chatDraftHistoryControl.canRedo}
                        onClick={chatDraftHistoryControl.onRedo}
                        aria-label={t('workstream_chat_draft_redo')}
                      >
                        <UIIcon
                          src={RedoLine}
                          size="small"
                          className={atoms({
                            color: !chatDraftHistoryControl.canRedo
                              ? 'Opacity Surface'
                              : 'Text Subtle',
                          })}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        open={addDocModalOpen}
        withCloseButton="close-add-context-doc"
        isCentered
        onRequestClose={() => setAddDocModalOpen(false)}
        width="small"
      >
        <Modal.Header>
          <Modal.Title>{t('workstream_chat_add_context_doc_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="12">
            <Text size="small" color="subtle">
              {t('workstream_chat_add_context_doc_helper')}
            </Text>
            {addDocError ? (
              <Text size="small" style={{color: 'var(--dig-color__text__critical)'}}>
                {addDocError}
              </Text>
            ) : null}
            <Stack gap="4">
              <Text size="xsmall" color="subtle">
                {t('workstream_chat_add_context_doc_url_label')}
              </Text>
              <TextInput
                value={addDocUrl}
                onChange={(e) => {
                  setAddDocUrl(e.target.value);
                  setAddDocUrlInvalid(false);
                  setAddDocError('');
                }}
                placeholder="https://"
                autoFocus
                isInvalid={addDocUrlInvalid || Boolean(addDocError)}
              />
            </Stack>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={() => setAddDocModalOpen(false)}>{t('cancel')}</CancelButton>
          <Button variant="primary" onClick={confirmAddDoc}>
            {t('workstream_chat_add_context_doc_submit')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={customInstructionsModalOpen}
        withCloseButton="close-custom-instructions"
        isCentered
        onRequestClose={() => setCustomInstructionsModalOpen(false)}
        width="small"
      >
        <Modal.Header>
          <Modal.Title>{t('workstream_chat_custom_instructions_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="12">
            <Text size="small" color="subtle">
              {t('workstream_chat_custom_instructions_helper')}
            </Text>
            <TextArea
              rows={5}
              maxLength={4000}
              value={customInstructionsDraft}
              onChange={(e) => setCustomInstructionsDraft(e.currentTarget.value)}
              placeholder={t('workstream_chat_custom_instructions_placeholder')}
              autoFocus
            />
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={() => setCustomInstructionsModalOpen(false)}>
            {t('cancel')}
          </CancelButton>
          <Button
            variant="primary"
            onClick={() => {
              setCustomInstructions(customInstructionsDraft.trim());
              setCustomInstructionsModalOpen(false);
            }}
          >
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        marginTop="16"
        style={{gap: 2}}
      >
        <UIIcon src={DropboxDashLine} size="small" style={{flexShrink: 0, marginTop: 1}} />
        <Text color="subtle" size="xsmall">
          {t('workstream_chat_footer_disclaimer')}
        </Text>
      </Box>
    </Box>
  );
};
